import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompetition } from '../context/CompetitionContext';
import { Discipline } from '../firebase/competitionService';

const DISCIPLINE_CARDS: Record<Discipline, {
  label: string;
  icon: string;
  resultsPath: string;
  adminPath: string;
  resultColor: string;
  adminColor: string;
  description: string;
}> = {
  boulder: {
    label:       'Boulder',
    icon:        '🪨',
    resultsPath: 'results/boulder',
    adminPath:   'boulder-admin',
    resultColor: 'from-green-500 to-teal-600',
    adminColor:  'from-green-700 to-teal-800',
    description: 'Zone and top scores per problem',
  },
  lead: {
    label:       'Lead',
    icon:        '🧗',
    resultsPath: 'results/lead',
    adminPath:   'lead-admin',
    resultColor: 'from-blue-500 to-indigo-600',
    adminColor:  'from-blue-700 to-indigo-800',
    description: 'The higher the better',
  },
  speed: {
    label:       'Speed',
    icon:        '⚡',
    resultsPath: 'speed-schedule',
    adminPath:   'speed-admin',
    resultColor: 'from-yellow-500 to-orange-500',
    adminColor:  'from-yellow-700 to-orange-700',
    description: 'Qualification heats and finals bracket',
  },
};

const CompetitionHubPage: React.FC = () => {
  const navigate = useNavigate();
  const { competitionId, metadata, isAdmin, adminSecret } = useCompetition();

  const [loginOpen,  setLoginOpen]  = useState(false);
  const [loginInput, setLoginInput] = useState('');
  const [loginError, setLoginError] = useState(false);

  const handleLogin = () => {
    if (loginInput === metadata?.adminSecret) {
      navigate(`/competition/${competitionId}?secret=${loginInput}`);
    } else {
      setLoginError(true);
    }
  };

  const secretSuffix = adminSecret ? `?secret=${adminSecret}` : '';
  const disciplines  = metadata?.disciplines ?? (['boulder', 'lead', 'speed'] as Discipline[]);

  const formatDate = (d: string) => {
    try { return new Date(d).toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' }); }
    catch { return d; }
  };

  return (
    <div className="min-h-screen bg-dark text-white">
      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold tracking-tight">
            {metadata?.name ?? competitionId}
          </h1>
          {(metadata?.date || metadata?.location) && (
            <p className="text-gray-400 mt-2 text-lg">
              {metadata.date && formatDate(metadata.date)}
              {metadata.date && metadata.location && ' · '}
              {metadata.location}
            </p>
          )}
        </div>

        {/* Timetable */}
        {metadata?.timetable && metadata.timetable.length > 0 && (
          <>
            <h2 className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-4">Timetable</h2>
            <div className="bg-gray-900 rounded-2xl overflow-hidden mb-10 divide-y divide-gray-800">
              {[...metadata.timetable].sort((a, b) => a.start.localeCompare(b.start)).map((row, i) => (
                <div key={i} className={`flex items-center gap-4 px-5 py-3 ${row.highlight ? 'bg-gray-900' : 'bg-gray-800/50'}`}>
                  <span className={`font-mono text-sm font-bold shrink-0 w-20 text-right ${row.highlight ? 'text-blue-300' : 'text-gray-500'}`}>
                    {row.start}
                  </span>
                  <span className="text-gray-600 text-xs shrink-0">→</span>
                  <span className={`font-mono text-xs shrink-0 w-16 ${row.highlight ? 'text-gray-400' : 'text-gray-600'}`}>
                    {row.end}
                  </span>
                  <span className={`text-sm flex-1 ${row.highlight ? 'text-white font-semibold' : 'text-gray-400'}`}>
                    {row.activity}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Results cards */}
        <h2 className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-4">Live Results</h2>
        <div className="grid gap-5 sm:grid-cols-3 mb-10">
          {disciplines.map(d => {
            const card = DISCIPLINE_CARDS[d];
            if (!card) return null;
            return (
              <button
                key={d}
                onClick={() => navigate(`/competition/${competitionId}/${card.resultsPath}`)}
                className={`bg-gradient-to-br ${card.resultColor} rounded-2xl p-6 text-left shadow-lg hover:scale-105 active:scale-95 transition-transform duration-150 focus:outline-none focus:ring-2 focus:ring-white`}
              >
                <div className="text-4xl mb-3">{card.icon}</div>
                <h3 className="text-lg font-bold mb-1">{card.label}</h3>
                <p className="text-sm text-white/80 leading-snug">{card.description}</p>
              </button>
            );
          })}
        </div>

        {/* Admin section (shown only to admin) */}
        {isAdmin && (
          <>
            <h2 className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-4">Admin Panel</h2>
            <div className="grid gap-5 sm:grid-cols-3 mb-4">
              {disciplines.map(d => {
                const card = DISCIPLINE_CARDS[d];
                if (!card) return null;
                return (
                  <button
                    key={d}
                    onClick={() => navigate(`/competition/${competitionId}/${card.adminPath}${secretSuffix}`)}
                    className={`bg-gradient-to-br ${card.adminColor} rounded-2xl p-5 text-left shadow-lg hover:scale-105 active:scale-95 transition-transform duration-150 focus:outline-none focus:ring-2 focus:ring-white`}
                  >
                    <div className="text-3xl mb-2">{card.icon}</div>
                    <h3 className="text-base font-bold mb-0.5">{card.label} Admin</h3>
                  </button>
                );
              })}
            </div>
            {disciplines.includes('lead') && (
              <button
                onClick={() => navigate(`/competition/${competitionId}/lead-jury-print${secretSuffix}`)}
                className="w-full sm:w-auto bg-gradient-to-br from-slate-600 to-slate-800 rounded-2xl px-6 py-4 text-left shadow-lg hover:scale-105 active:scale-95 transition-transform duration-150 focus:outline-none focus:ring-2 focus:ring-white"
              >
                <span className="text-2xl mr-3">🖨</span>
                <span className="font-bold">Lead Print Sheet</span>
              </button>
            )}
          </>
        )}
        {/* Login section (non-admin) */}
        {!isAdmin && (
          <div className="mt-10 border-t border-gray-800 pt-8 text-center">
            {!loginOpen ? (
              <button
                onClick={() => setLoginOpen(true)}
                className="text-gray-500 hover:text-gray-300 text-sm underline transition-colors"
              >
                Admin login
              </button>
            ) : (
              <div className="inline-flex flex-col items-center gap-3">
                <p className="text-gray-400 text-sm">Enter the organiser password</p>
                <div className="flex gap-2">
                  <input
                    type="password"
                    value={loginInput}
                    onChange={e => { setLoginInput(e.target.value); setLoginError(false); }}
                    onKeyDown={e => e.key === 'Enter' && handleLogin()}
                    placeholder="Password"
                    autoFocus
                    className="bg-gray-800 border border-gray-600 rounded-lg px-4 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondary w-52"
                  />
                  <button
                    onClick={handleLogin}
                    className="bg-secondary hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                  >
                    Login
                  </button>
                </div>
                {loginError && <p className="text-red-400 text-xs">Incorrect password.</p>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompetitionHubPage;
